import { QueryClient } from '@tanstack/react-query';
import { framesKeys } from '@/features/frames-to-order/hooks/useFrames';
import { orderingKeys } from '@/features/weekly-ordering/hooks/useOrdering';
import { receivingKeys } from '@/features/receiving/hooks/useReceiving';

/**
 * Centralized query invalidation helper for frame status changes.
 *
 * Any mutation that changes frame status (PENDING → ORDERED → RECEIVED) should call
 * this function to ensure all affected views are updated automatically.
 *
 * This invalidates:
 * - Frames to Order queries (framesKeys.lists)
 * - Weekly Ordering queries (orderingKeys.all)
 * - Receiving queries (receivingKeys.all)
 * - Any dashboard/report queries that depend on status counts
 *
 * @param queryClient - The React Query client instance
 *
 * @example
 * ```typescript
 * export function useMarkAsOrdered() {
 *   const queryClient = useQueryClient();
 *   return useMutation({
 *     mutationFn: (input: MarkAsOrderedInput) => framesService.markAsOrdered(input),
 *     onSuccess: () => {
 *       invalidateFrameStatusQueries(queryClient);
 *     },
 *   });
 * }
 * ```
 */
export function invalidateFrameStatusQueries(queryClient: QueryClient): void {
  // Invalidate all frame lists (affects Frames to Order page)
  queryClient.invalidateQueries({ queryKey: framesKeys.lists() });

  // Invalidate ordering queries (affects Weekly Ordering page)
  queryClient.invalidateQueries({ queryKey: orderingKeys.all });

  // Invalidate receiving queries (affects Receiving page)
  queryClient.invalidateQueries({ queryKey: receivingKeys.all });

  // Note: If you add dashboard/report queries that depend on frame status counts,
  // add their invalidation here as well.
}
